import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBedrockRegistration } from 'store';
import { SocialLogin } from 'components/AccountLoginRegistration/SocialLogin';
import { RecaptchaForm } from 'components/AccountLoginRegistration/RecaptchaForm';
import { TermsAndConditions } from 'components/AccountLoginRegistration/TermsAndConditions';
import { InteractiveTextLink } from 'components/InteractiveTextLink';
import { SubmitButton } from 'components/FormFields/SubmitButton';
import { TextField } from 'components/FormFields/TextField';
import { Checkbox } from 'components/FormFields/Checkbox';
import { ErrorMsg } from 'components/FormFields/ErrorMsg';
import { Description } from 'components/FormFields/Description';
import { pianoContainerID } from 'lib/Piano/pianoConfig';
import { useVertical } from 'lib/Hooks';
import { SCREENS } from 'lib/loginFormConstants';
import { publisherNameMap } from 'lib/vertical';

import {
  registrationSchema,
} from '../validationSchema';

/**
 * Renders the account creation form with email, password, terms, and social login options.
 *
 * @param {object} props - The component props.
 * @param {string} props.entryTitle - The title to display at the top of the form.
 * @param {string} props.pageView - The current page view identifier.
 * @param {string} props.currentScreen - The current screen identifier.
 * @returns {JSX.Element} The rendered account creation form screen.
 */
const RegisterScreen = ({ entryTitle, pageView, currentScreen }) => {
  const showAuthenticationModal = useBedrockRegistration((state) => state.showAuthenticationModal);
  const userRegistration = useBedrockRegistration((state) => state.userRegistration);
  const setScreen = useBedrockRegistration((state) => state.setScreen);
  const track = useBedrockRegistration((state) => state.track);
  const error = useBedrockRegistration((state) => state.error);
  const setEmail = useBedrockRegistration((state) => state.setEmail);
  const { vertical } = useVertical();
  const brand = publisherNameMap[vertical];

  const [userEmail, setUserEmail] = useState('');

  const methods = useForm({
    resolver: yupResolver(registrationSchema, { userEmail }),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  /**
   * Handles form submission for account creation.
   * @param {object} data - The form data.
   */
  const onSubmit = (data) => {
    const registrationPayload = {
      method: 'SubmitButton:Click',
      registrationObj: {
        email: data.email,
        password: data.password,
        brand,
        source: 'default',
      },
    };
    userRegistration(registrationPayload).then((result) => {
      if (result) {
        setScreen(result);
        setEmail(data.email);
      }
    });
  };

  const emailInput = methods.watch('email');

  useEffect(() => {
    // update the user input email to check against password validation
    setUserEmail(emailInput);
  }, [emailInput]);

  return (
    <>
      <div data-testid="account-registration">
        {entryTitle && (
          <div id={pianoContainerID.FORM_TITLE} data-testid="formTitle">
            <h2>{entryTitle}</h2>
          </div>
        )}
        <ErrorMsg message={error} />
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <RecaptchaForm
            submitCallback={onSubmit}
          >
            <Description>
              Already have an account?&nbsp;
              <InteractiveTextLink
                onClick={() => {
                  track({
                    action: 'SignUp:Submit',
                    method: 'SignIn:Click',
                  });
                  setScreen(SCREENS.LOGIN, {
                    email: methods.getValues('email'),
                    password: methods.getValues('password'),
                  });
                }}
                dataTestid="sign-in-button"
              >
                Sign In
              </InteractiveTextLink>
            </Description>
            <TextField
              name="email"
              type="email"
              dataTestId="email-input-field"
              placeholder="Email"
              autoFocus={showAuthenticationModal}
              isRequired
            />
            <TextField
              name="password"
              type="create-password"
              dataTestId="password-input-field"
              placeholder="Password"
              isRequired
            />
            <Checkbox name="termsAndConditions" dataTestId="terms-checkbox" isRequired>
              <TermsAndConditions />
            </Checkbox>
            <SubmitButton
              title="Create Account"
            />
          </RecaptchaForm>
        </FormProvider>
        <SocialLogin
          pageView={pageView}
          currentScreen={currentScreen}
        />
      </div>
    </>
  );
};

export { RegisterScreen };
